import { computed, onMounted, ref } from 'vue';
import md5 from 'md5';
import { useApiFieldErrors, useI18n, useMixpanel, useCurrencyList } from '@/composables';
import { $emitter } from '@/services/emitter';
import { putOffersBulkUpdateApiRequest } from '@/services/api/offers-api';
import { populateTagsStoreCacheApiRequest } from '@/services/api/api-services/tags';
import availableTokens from '@/views/reports/forms/offer/config/availableTokens';
import parseApiErrors, {
  parseFlowErrorsRaw,
  translateFlowErrorsRaw,
} from '@/utils/parseApiErrors';
import { useTableQuery } from '@/composables/useTableQuery';
import { useStore } from '@/store';

type BulkEditForm = {
  path: string;
  query: string;
  domain: string;
  payoutType: string;
  payoutSum: number;
  currencyId: string;
  tags: Array<{ id?: string; name: string } | string>;
  protocol: string;
};

type CheckboxState = {
  domain: boolean;
  path: boolean;
  query: boolean;
  tags: boolean;
  currencyId: boolean;
  payoutType: boolean;
  protocol: boolean;
};

const defaultBulkForm = (currencyId: string): BulkEditForm => ({
  path: '',
  query: '',
  domain: '',
  payoutType: 'auto',
  payoutSum: 0,
  currencyId,
  tags: [],
  protocol: '',
});

const defaultCheckboxState = (): CheckboxState => ({
  domain: false,
  path: false,
  query: false,
  tags: false,
  currencyId: false,
  payoutType: false,
  protocol: false,
});

const dependentKeys: Record<string, string[]> = {
  currencyId: ['payoutType', 'payoutSum'],
};

export const useOfferBulkEdit = (emitClose: () => void) => {
  const { t, locale } = useI18n();
  const store = useStore();
  const tableQueryApi = useTableQuery();
  const { trackMixpanel } = useMixpanel();
  const { currencyListOrderedPreferred, currencies } = useCurrencyList();

  const { fieldErrors, errorsControlActive, errorsDict } = useApiFieldErrors();

  const form = ref<BulkEditForm>(defaultBulkForm(store.user.currencyId));
  const checkboxState = ref<CheckboxState>(defaultCheckboxState());
  const show = ref(false);
  const errorsRaw = ref<unknown[][] | null>(null);
  const errorMessage = ref('');
  const activeWorkspaceId = ref('');
  const oldHash = ref('');

  const selectedItems = computed(() =>
    (tableQueryApi.tableSelectedItems.value as Array<{ isDirect?: boolean }>).filter(
      ({ isDirect }) => !isDirect,
    ),
  );

  const chips = computed(() =>
    availableTokens().map((tag) => ({
      tag,
      disabled: false,
    })),
  );

  const errors = computed(() =>
    translateFlowErrorsRaw(errorsRaw.value, errorMessage.value),
  );

  const activeElements = computed(() =>
    Object.entries(checkboxState.value).reduce(
      (accumulator, [key, value]) => {
        if (value) {
          const dependentValues = dependentKeys[key]?.reduce(
            (dependentAccumulator, dependentKey) => {
              dependentAccumulator[dependentKey] =
                form.value[dependentKey as keyof BulkEditForm];
              return dependentAccumulator;
            },
            {} as Record<string, unknown>,
          );

          return {
            ...accumulator,
            [key]: form.value[key as keyof BulkEditForm],
            ...dependentValues,
          };
        }
        return accumulator;
      },
      {} as Record<string, unknown>,
    ),
  );

  const getCurrentHash = () =>
    md5(JSON.stringify({ ...form.value, ...checkboxState.value }));

  const load = async () => {
    form.value.currencyId = currencyListOrderedPreferred.value[0]?.code || store.user.currencyId;
  };

  const close = () => {
    emitClose();
    $emitter.emit('clear-report-selection');
  };

  const checkClose = () => {
    if (oldHash.value !== getCurrentHash()) {
      $emitter.emit('open-dialog', {
        name: 'ConfirmDialog',
        data: { onConfirm: () => close() },
      });
    } else {
      close();
    }
  };

  const trackAnalytics = (event: string, elementCount: number) => {
    const {
      domain: domainEnabled,
      path: pathEnabled,
      query: queryParamsTokenEnabled,
      tags: tagsEnabled,
      currencyId: currencyEnabled,
    } = checkboxState.value;

    trackMixpanel(event, {
      element_count: elementCount,
      domain_enabled: domainEnabled,
      path_enabled: pathEnabled,
      query_params_token_enabled: queryParamsTokenEnabled,
      tags_enabled: tagsEnabled,
      currency_enabled: currencyEnabled,
    });
  };

  const submit = async () => {
    const ids = selectedItems.value.map(({ DT_RowId }) => DT_RowId);
    const params: {
      ids: string[];
      tags?: Array<string | { id: string; name: string }>;
    } = {
      ids,
      ...activeElements.value,
    };

    if (params.tags) {
      params.tags = params.tags.map((item) =>
        typeof item === 'string' ? item : item.name,
      );
    }

    try {
      await putOffersBulkUpdateApiRequest(activeWorkspaceId.value, params);
      trackAnalytics('offer_bulk_edit', ids.length);
      close();
      $emitter.emit('refresh-report');
      $emitter.emit('toast-success', t('Changes have been successfully saved'));
    } catch (error) {
      store.setLoading(false);
      const parsedFieldErrors = parseApiErrors({ error, nested: false });
      if (Object.keys(parsedFieldErrors).length === 0) {
        return;
      }

      fieldErrors.value = parsedFieldErrors;
      const responseData = (error as { response?: { data?: { errors?: unknown; message?: string } } })
        ?.response?.data;

      if (responseData?.errors) {
        errorsRaw.value = parseFlowErrorsRaw(responseData.errors);
        errorMessage.value = responseData.message || '';
        errorsControlActive.value = true;
      }
    }
  };

  onMounted(async () => {
    if (!store.cache.tags.offers.isUploaded) {
      await populateTagsStoreCacheApiRequest({ type: 'offers' });
    }

    store.setLoading(true);

    activeWorkspaceId.value = selectedItems.value[0].DT_RowWorkspaceId;

    await load();

    store.setLoading(false);
    show.value = true;
    oldHash.value = getCurrentHash();
  });

  return {
    t,
    locale,
    store,

    show,
    form,
    checkboxState,
    selectedItems,
    currencyListOrderedPreferred,
    currencies,
    chips,
    errors,
    errorsDict,
    errorsControlActive,
    checkClose,
    submit,
  };
};
